import {
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { Ref } from "react";
import { Platform, View } from "react-native";

import Icon from "$src/core/components/atoms/Icon/Icon";
import Pressable from "$src/core/components/atoms/Pressable";
import TextInput from "$src/core/components/atoms/TextInput";
import useDebouncedCallback from "$src/core/hooks/use-debounced-callback";
import useTranslation from "$src/core/hooks/use-translation";

import ListeningIndicator from "./ListeningIndicator";
import $ from "./SearchBox.styles";
import VoiceSearchButton from "./VoiceSearchButton";

type VoiceSearchProps = {
  isListening: boolean;
  audioLevel: number;
  onMicPress: () => void;
};

type Props = {
  ref?: Ref<SearchBoxRef>;
  placeholder?: string;
  defaultValue?: string;
  onSearch: (query: string) => void;
  onClear?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  readonly?: boolean;
  accessibilityLabel?: string;
  voiceSearch?: VoiceSearchProps;
};

export type SearchBoxRef = {
  setValue: (value: string) => void;
  focus: () => void;
};

const TV_SEARCH_AS_YOU_TYPE_MIN_LENGTH = 4;
const TV_SEARCH_DEBOUNCE_MS = 500;

const SearchBox = ({
  ref: forwardedRef,
  onSearch,
  placeholder,
  defaultValue,
  readonly,
  onFocus,
  onBlur,
  onClear,
  accessibilityLabel,
  voiceSearch,
}: Props) => {
  const { t } = useTranslation();

  const [query, setQuery] = useState(defaultValue);

  const textInputRef = useRef<View | null>(null);

  const isClearVisible = !!(query && !readonly && onClear && !Platform.isTV);
  const isListening = voiceSearch?.isListening ?? false;
  const isListeningOverlay = isListening && !query;

  useImperativeHandle(forwardedRef, () => ({
    setValue: (value: string) => setQuery(value),
    focus: () => textInputRef.current?.focus(),
  }));

  const onSearchRef = useRef(onSearch);
  const lastAutoSearchedRef = useRef<string | null>(null);

  /* Let the debounced search reach the latest onSearch without re-arming its timer. */
  useLayoutEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const autoSearch = useCallback((value: string) => {
    const trimmed = value.trim();
    // Skip a repeat of the query we last fired, so leaving the field (onEndEditing on TV) does
    // not search the same text a second time.
    if (!trimmed || trimmed === lastAutoSearchedRef.current) return;

    lastAutoSearchedRef.current = trimmed;
    onSearchRef.current(trimmed);
  }, []);

  const debouncedAutoSearch = useDebouncedCallback(
    autoSearch,
    TV_SEARCH_DEBOUNCE_MS,
  );

  // TV only: the D-pad keyboard makes submitting awkward, so search as you type once the query is
  // long enough to be worth a request. Phone and web still search on explicit submit.
  const handleChangeText = (text: string) => {
    setQuery(text);

    if (!text.trim()) {
      lastAutoSearchedRef.current = null;
      onClear?.();
      return;
    }

    if (
      Platform.isTV &&
      text.trim().length >= TV_SEARCH_AS_YOU_TYPE_MIN_LENGTH
    ) {
      debouncedAutoSearch(text);
    }
  };

  const handleSubmit = () => {
    if (query) onSearch(query);
  };

  const handleClear = () => {
    setQuery("");
    lastAutoSearchedRef.current = null;
    onClear?.();
  };

  /* Adopt a query pushed in from outside (deep link, voice search, history tap). */
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuery(defaultValue);
  }, [defaultValue]);

  const inputPlaceholder = isListeningOverlay
    ? ""
    : (placeholder ?? t("SEARCH/INPUT/PLACEHOLDER"));

  return (
    <View style={$.container}>
      <View style={$.inputWrapper}>
        <TextInput
          contentContainerStyle={[
            $.inputContentContainer,
            voiceSearch || isClearVisible ? $.inputWithVoice : null,
          ]}
          icon="search"
          placeholder={inputPlaceholder}
          value={query}
          onFocus={onFocus}
          onBlur={onBlur}
          onChangeText={handleChangeText}
          onSubmitEditing={handleSubmit}
          onEndEditing={Platform.isTV ? handleSubmit : undefined}
          returnKeyType="search"
          isRounded
          readOnly={readonly}
          accessibilityRole="search"
          accessibilityLabel={accessibilityLabel}
          {...(Platform.OS === "web" ? ({ role: "searchbox" } as any) : {})}
          {...(Platform.isTV ? {} : { ref: textInputRef })}
        />
        {isListeningOverlay && voiceSearch && (
          <View style={$.listeningOverlay}>
            <ListeningIndicator
              label={t("SEARCH/VOICE/LISTENING")}
              audioLevel={voiceSearch.audioLevel}
            />
          </View>
        )}
      </View>
      {voiceSearch && !query && (
        <View style={$.voiceControls({ withClear: isClearVisible })}>
          <VoiceSearchButton
            isListening={isListening}
            onPress={voiceSearch.onMicPress}
            accessibilityLabel={
              isListening
                ? t("SEARCH/VOICE/STOP_LISTENING")
                : t("SEARCH/VOICE/MIC_LABEL")
            }
            accessibilityHint={
              isListening
                ? t("SEARCH/VOICE/STOP_HINT")
                : t("SEARCH/VOICE/MIC_HINT")
            }
          />
        </View>
      )}
      {isClearVisible && (
        <Pressable
          style={$.inputActionPressable}
          onPress={handleClear}
          accessibilityRole="button"
          accessibilityLabel={t("SEARCH/INPUT/CLEAR")}
        >
          <Icon name="close" style={$.inputActionIcon} />
        </Pressable>
      )}
    </View>
  );
};

export default SearchBox;
